import React from 'react';
import { Calendar, Package, Tag, RotateCcw, RefreshCw, History, Archive, Clock, CheckCircle, AlertCircle } from 'lucide-react';

const TYPE_CONFIG = {
  kupovina: { label: 'Kupovina', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  prodaja: { label: 'Prodaja', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  razmena: { label: 'Razmena', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
};

const STATUS_CONFIG = {
  archived: { label: 'Arhiviran', bg: 'bg-gray-100', text: 'text-gray-600', icon: Archive },
  expired: { label: 'Istekao', bg: 'bg-orange-100', text: 'text-orange-700', icon: Clock },
  completed: { label: 'Završen', bg: 'bg-teal-100', text: 'text-teal-700', icon: CheckCircle },
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d?.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatPrice(price, currency) {
  if (price === null || price === undefined) return 'Po dogovoru';
  return `${Number(price)?.toLocaleString('sr-RS')} ${currency || 'RSD'}`;
}

export default function ArchiveOfferCard({ offer, onRestore, onRelist, onHistory, isRestoring, isRelisting }) {
  const typeConf = TYPE_CONFIG?.[offer?.wishType] || TYPE_CONFIG?.prodaja;
  const statusConf = STATUS_CONFIG?.[offer?.offerStatus] || STATUS_CONFIG?.archived;
  const StatusIcon = statusConf?.icon;
  const showRestore = offer?.offerStatus === 'archived' || offer?.offerStatus === 'expired';
  const showHistory = offer?.offerStatus === 'completed';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
      {/* Status badge */}
      <div className="flex items-center justify-between mb-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusConf?.bg} ${statusConf?.text}`}>
          <StatusIcon size={12} />
          {statusConf?.label}
        </span>
        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${typeConf?.bg} ${typeConf?.text} ${typeConf?.border}`}>
          {typeConf?.label}
        </span>
      </div>

      {/* Title */}
      <div className="mb-3">
        <h3 className="font-semibold text-gray-900 text-base">
          {offer?.voucherName || 'Nepoznat vaučer'}
        </h3>
        {offer?.voucherSymbol && (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500 mt-0.5">
            <Tag size={11} />
            {offer?.voucherSymbol}
          </span>
        )}
        {offer?.category && (
          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs bg-indigo-50 text-indigo-600 border border-indigo-100">
            {offer?.category}
          </span>
        )}
      </div>

      {/* Details */}
      <div className="space-y-1.5 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-1.5 text-gray-500">
            <Package size={13} className="text-gray-400" />
            Količina
          </span>
          <span className="font-medium text-gray-800">
            {offer?.tokenQuantity ? offer?.tokenQuantity?.toLocaleString('sr-RS') : 'N/A'}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Cena</span>
          <span className="font-semibold text-teal-700">{formatPrice(offer?.maxPrice, offer?.currency)}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-1.5 text-gray-500">
            <Calendar size={13} className="text-gray-400" />
            Objavljeno
          </span>
          <span className="text-gray-600">{formatDate(offer?.createdAt)}</span>
        </div>
        {offer?.expiryDate && (
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-orange-500">
              <AlertCircle size={13} />
              {offer?.offerStatus === 'expired' ? 'Isteklo' : 'Rok'}
            </span>
            <span className="text-orange-600 font-medium">{formatDate(offer?.expiryDate)}</span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
        {showRestore && (
          <button
            onClick={() => onRestore?.(offer?.id)}
            disabled={isRestoring}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <RotateCcw size={13} className={isRestoring ? 'animate-spin' : ''} />
            Vrati oglas
          </button>
        )}
        <button
          onClick={() => onRelist?.(offer)}
          disabled={isRelisting}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-700 border border-gray-300 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60"
        >
          <RefreshCw size={13} className={isRelisting ? 'animate-spin' : ''} />
          Ponovo objavi
        </button>
        {showHistory && (
          <button
            onClick={() => onHistory?.(offer)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-medium rounded-lg hover:bg-indigo-100 transition-colors"
          >
            <History size={13} />
            Istorija
          </button>
        )}
      </div>
    </div>
  );
}
